import { inject, Injectable } from '@angular/core';

import { AuthConfigService } from '../services';
import { BackendAbstractService } from './abstract/backend-abstract.service';
import { AuthBackendHandler } from './handlers/auth-backend-handler/auth-backend.handler';
import { LegacyAuthBackendHandler } from './handlers/legacy-auth/legacy-auth-backend-handler';
import { Observable } from 'rxjs';
import {
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  AuthProvider,
  Credentials,
  LegacyLoginResponse,
  LoginResponseInterface,
  RefreshTokenRequest,
  SmsChallengeRequest,
} from '../models';
import { SetupMfaRequest } from '../models/setup-mfa-request.model';
import { SsoLogin } from '../models/sso-login.model';

@Injectable({
  providedIn: 'root',
})
export class AuthBackend {
  #authConfigService = inject(AuthConfigService);

  #handlers: Map<AuthProvider, BackendAbstractService<unknown>> = new Map<AuthProvider, BackendAbstractService<unknown>>([
    [AuthProvider.Cognito, inject(AuthBackendHandler)],
    [AuthProvider.Legacy, inject(LegacyAuthBackendHandler)],
  ]);

  activateAccount(confirmCode: string): Observable<null> {
    // TO BE IMPLEMENTED
    return this.#handlers.get(AuthProvider.Legacy).activateAccount(confirmCode);
  }

  login(credentials: Credentials, usertype: string): Observable<LoginResponseInterface> {
    return this.getHandlerByConfig()?.login(credentials, usertype);
  }

  loginWithSso(authCode: string, websiteId: number): Observable<LoginResponseInterface> {
    return (this.#handlers.get(AuthProvider.Cognito) as AuthBackendHandler)?.loginWithSso(authCode, websiteId);
  }

  impersonate(credentials: Credentials, usertype: string): Observable<LegacyLoginResponse> {
    const handler = credentials?.username
      ? this.#handlers.get(AuthProvider.Legacy)
      : this.#handlers.get(AuthProvider.Cognito);
    return handler.impersonate(credentials, usertype) as Observable<LegacyLoginResponse>;
  }

  requestPassword(email: string): Observable<null> {
    return this.getHandlerByConfig()?.requestPassword(email);
  }

  resetPassword(password: string, confirmationCode: string, email?: string): Observable<null> {
    return this.getHandlerByConfig()?.resetPassword(password, confirmationCode, email);
  }

  changePassword(currentPassword: string, password: string, accessToken: string): Observable<void> {
    return (this.#handlers.get(AuthProvider.Cognito) as AuthBackendHandler)?.changePassword(
      currentPassword,
      password,
      accessToken,
    );
  }

  signup(formData: unknown): Observable<null> {
    return this.#handlers.get(AuthProvider.Legacy).signup(formData);
  }

  refreshToken(refreshToken: RefreshTokenRequest): Observable<LoginResponseInterface> {
    return (this.#handlers.get(AuthProvider.Cognito) as AuthBackendHandler)?.refreshToken(refreshToken);
  }

  confirmCode(request: SmsChallengeRequest): Observable<LoginResponseInterface> {
    return (this.#handlers.get(AuthProvider.Cognito) as AuthBackendHandler)?.confirmCode(request);
  }

  getDomain(username: string): Observable<string> {
    return this.#handlers.get(AuthProvider.Cognito)?.getDomain(username);
  }

  setupMfa(request: SetupMfaRequest): Observable<void> {
    return (this.#handlers.get(AuthProvider.Cognito) as AuthBackendHandler)?.setupMfa(request);
  }

  getSsoLogin(websiteId: number, websiteUrl: string): Observable<SsoLogin> {
    return (this.#handlers.get(AuthProvider.Cognito) as AuthBackendHandler)?.getSsoLogin(websiteId, websiteUrl);
  }

  validateInvitation(token: string): Observable<AcceptInvitationResponse> {
    return (this.#handlers.get(AuthProvider.Cognito) as AuthBackendHandler)?.validateInvitation(token);
  }

  acceptInvitation(acceptInvitation: AcceptInvitationRequest): Observable<unknown> {
    return (this.#handlers.get(AuthProvider.Cognito) as AuthBackendHandler)?.acceptInvitation(acceptInvitation);
  }

  private getHandlerByConfig(): BackendAbstractService<any> | undefined {
    return this.#handlers.get(this.#authConfigService.config.authProvider);
  }
}
